import { useState, useEffect } from "react";

import { formatDistanceToNow } from "date-fns";
import { Loader2, ShieldAlert } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";


interface SessionTermination {
  id: string;
  user_id: string;
  reason: string;
  trigger_event: string;
  metadata: any;
  ip_address: string;
  created_at: string;
  user_email?: string;
}

export const SessionTerminationsMonitor = () => {
  const [terminations, setTerminations] = useState<SessionTermination[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    loadTerminations();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('session-terminations')
      .on(
        'postgres_changes' as any,
        {
          event: 'INSERT',
          schema: 'public',
          table: 'session_terminations' as any
        },
        () => {
          loadTerminations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [filter]);

  const loadTerminations = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('session_terminations' as any)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (filter !== 'all') {
        query = query.eq('reason', filter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Failed to load session terminations:', error);
        return;
      }

      // Fetch user emails separately
      const userIds = [...new Set((data || []).map((t: any) => t.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, email')
        .in('id', userIds);

      const emailMap = new Map(profiles?.map(p => [p.id, p.email]) || []);
      
      const terminationsWithEmail = (data || []).map((t: any) => ({
        ...t,
        user_email: emailMap.get(t.user_id) || 'Unknown'
      }));

      setTerminations(terminationsWithEmail as any);
    } catch (error) {
      console.error('Error loading session terminations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getReasonBadgeVariant = (reason: string) => {
    switch (reason) {
      case 'impossible_travel':
      case 'suspicious_location':
        return 'destructive';
      case 'user_revoked':
      case 'user_revoked_all':
        return 'default';
      case 'expired':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" />
              Session Terminations
            </CardTitle>
            <CardDescription>
              Monitor all session termination events across the platform
            </CardDescription>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by reason" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reasons</SelectItem>
              <SelectItem value="impossible_travel">Impossible Travel</SelectItem>
              <SelectItem value="suspicious_location">Suspicious Location</SelectItem>
              <SelectItem value="user_revoked">User Revoked</SelectItem>
              <SelectItem value="user_revoked_all">User Revoked All</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : terminations.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No session terminations found
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Trigger Event</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {terminations.map((termination) => (
                  <TableRow key={termination.id}>
                    <TableCell className="font-mono text-sm">
                      {termination.user_email || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getReasonBadgeVariant(termination.reason)}>
                        {termination.reason.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {termination.trigger_event || '-'}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {termination.ip_address}
                    </TableCell>
                    <TableCell className="text-sm">
                      {termination.metadata?.distance_km && (
                        <div className="text-xs">
                          Distance: {Math.round(termination.metadata.distance_km)} km
                        </div>
                      )}
                      {termination.metadata?.terminated_via && (
                        <div className="text-xs text-muted-foreground">
                          Via: {termination.metadata.terminated_via}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(termination.created_at), { addSuffix: true })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
